import { GEBKeys } from 'shur-api'

const BASE = 'shurDetails.sections.BUILDING'
const DEDUCTIBLES = `${BASE}.DEDUCTIBLES`
const COVERAGE = `${BASE}.COVERAGE`
const PLACE = 'shurDetails.sections.insuredPlace'

/**
 * Maps every used GEBKey to its i18n label path.
 * CoverageConcept and BuildingType are excluded – they are handled separately
 * in the creator due to custom transformation logic.
 */
export const BUILDING_KEY_LABEL_MAP: Partial<Record<GEBKeys, string>> = {
  // --- SCOPE_OF_INSURANCE (non-deductible) ---
  [GEBKeys.InstantCoverage]: `${BASE}.INSTANT_COVERAGE`,

  // --- DEDUCTIBLES (rendered in SCOPE_OF_INSURANCE block) ---
  [GEBKeys.BuildcovUnnamedPerilDeductible]: `${DEDUCTIBLES}.BUILDCOV_UNNAMED_PERIL_DEDUCTIBLE`,
  [GEBKeys.DeductibleBurglary]: `${DEDUCTIBLES}.DEDUCTIBLE_BURGLARY`,
  [GEBKeys.DeductibleFire]: `${DEDUCTIBLES}.DEDUCTIBLE_FIRE`,
  [GEBKeys.DeductibleGraffiti]: `${DEDUCTIBLES}.DEDUCTIBLE_GRAFFITI`,
  [GEBKeys.DeductibleHail]: `${DEDUCTIBLES}.DEDUCTIBLE_HAIL`,
  [GEBKeys.DeductibleNaturalharzard]: `${DEDUCTIBLES}.DEDUCTIBLE_NATURALHARZARD`,
  [GEBKeys.DeductibleOvervoltage]: `${DEDUCTIBLES}.DEDUCTIBLE_OVERVOLTAGE`,
  [GEBKeys.DeductiblePerBuilding]: `${DEDUCTIBLES}.DEDUCTIBLE_PER_BUILDING`,
  [GEBKeys.DeductibleTech]: `${DEDUCTIBLES}.DEDUCTIBLE_TECH`,
  [GEBKeys.DeductibleWater]: `${DEDUCTIBLES}.DEDUCTIBLE_WATER`,

  // --- PLACE (insuredPlace block) ---
  [GEBKeys.StreetAndNumber]: `${PLACE}.STREET_AND_NUMBER`,
  [GEBKeys.ZipcodeAndCity]: `${PLACE}.ZIPCODE_AND_CITY`,
  [GEBKeys.LivingArea]: `${BASE}.LIVING_AREA`,

  // --- Versicherte Gefahren Wohngebäude ---
  [GEBKeys.BuildcovNaturalharzardPeril]: `${COVERAGE}.BUILDCOV_NATURALHARZARD_PERIL`,
  [GEBKeys.BuildcovStormPeril]: `${COVERAGE}.BUILDCOV_STORM_PERIL`,
  [GEBKeys.BuildcovWaterPeril]: `${COVERAGE}.BUILDCOV_WATER_PERIL`,
  [GEBKeys.BuildingCovFirePeril]: `${COVERAGE}.BUILDING_COV_FIRE_PERIL`,
  [GEBKeys.BuildingCovOverloadPeril]: `${COVERAGE}.BUILDING_COV_OVERLOAD_PERIL`,

  // --- COVERAGE (Versicherte Gefahren und Deckungen block) ---
  [GEBKeys.BuildcovUnnamedPeril]: `${COVERAGE}.BUILDCOV_UNNAMED_PERIL`,
  [GEBKeys.AcHeatpumpCoverage]: `${COVERAGE}.AC_HEATPUMP_COVERAGE`,
  [GEBKeys.AdditionalAccessoriesCoverage]: `${COVERAGE}.ADDITIONAL_ACCESSORIES_COVERAGE`,
  [GEBKeys.AdditionalAccessoriesNaturalharzardCoverage]: `${COVERAGE}.ADDITIONAL_ACCESSORIES_NATURALHARZARD_COVERAGE`,
  [GEBKeys.DrainPipesOnPropertyCoverage]: `${COVERAGE}.DRAIN_PIPES_ON_PROPERTY_COVERAGE`,
  [GEBKeys.DrainPipesOutsidePropertyCoverage]: `${COVERAGE}.DRAIN_PIPES_OUTSIDE_PROPERTY_COVERAGE`,
  [GEBKeys.FireShellConstructionCoverageEnd]: `${COVERAGE}.FIRE_SHELL_CONSTRUCTION_COVERAGE_END`,
  [GEBKeys.FireShellConstructionCoverage]: `${COVERAGE}.FIRE_SHELL_CONSTRUCTION_COVERAGE`,
  [GEBKeys.GarageCoverage]: `${COVERAGE}.GARAGE_COVERAGE`,
  [GEBKeys.GrafittiDamageCoverage]: `${COVERAGE}.GRAFITTI_DAMAGE_COVERAGE`,
  [GEBKeys.HeritageProtectionCoverage]: `${COVERAGE}.HERITAGE_PROTECTION_COVERAGE`,
  [GEBKeys.Homeassistance]: `${COVERAGE}.HOMEASSISTANCE`,
  [GEBKeys.PhotovoltaicCoverage]: `${COVERAGE}.PHOTOVOLTAIC_COVERAGE`,
  [GEBKeys.ReplacementValue]: `${BASE}.REPLACEMENT_VALUE`,
  [GEBKeys.SmallOutbuildCoverage]: `${COVERAGE}.SMALL_OUTBUILD_COVERAGE`,
  [GEBKeys.TechCoverage]: `${COVERAGE}.TECH_COVERAGE`,
  [GEBKeys.TheftCoverage]: `${COVERAGE}.THEFT_COVERAGE`
}

// Versicherungsumfang - Sortierung nach Kommentar in https://jira.zurich.com/browse/KPTWO-6524
export const BUILDING_SCOPE_KEYS: GEBKeys[] = [
  GEBKeys.BuildcovUnnamedPeril,
  GEBKeys.DeductiblePerBuilding,
  GEBKeys.DeductibleNaturalharzard,
  GEBKeys.BuildcovUnnamedPerilDeductible,
  GEBKeys.DeductibleWater,
  GEBKeys.DeductibleFire,
  GEBKeys.DeductibleHail,
  GEBKeys.DeductibleOvervoltage,
  GEBKeys.DeductibleBurglary,
  GEBKeys.DeductibleTech,
  GEBKeys.DeductibleGraffiti,
  GEBKeys.FireShellConstructionCoverage,
  GEBKeys.FireShellConstructionCoverageEnd
]

/**
 * Versicherte Gefahren Wohngebäude - Sortierung nach Kommentar in https://jira.zurich.com/browse/KPTWO-6524
 * ACHTUNG: Diese Werte werden als Value unter dem Key COVERAGE.title angezeigt
 * siehe src/utils/shur/SHURDataBuildingCreator.ts - createCoveredPerilsField()
 */
export const ALL_COVERED_PERILS: GEBKeys[] = [
  GEBKeys.BuildingCovFirePeril,
  GEBKeys.BuildcovWaterPeril,
  GEBKeys.BuildcovStormPeril,
  GEBKeys.BuildingCovOverloadPeril,
  GEBKeys.BuildcovNaturalharzardPeril
]

// Versicherungsort
export const BUILDING_PLACE_KEYS: GEBKeys[] = [
  GEBKeys.StreetAndNumber,
  GEBKeys.ZipcodeAndCity,
  GEBKeys.LivingArea
]

// Versicherte Sachen - Sortierung nach Kommentar in https://jira.zurich.com/browse/KPTWO-6524
export const BUILDING_COVERAGE_KEYS: GEBKeys[] = [
  GEBKeys.ReplacementValue,
  GEBKeys.Homeassistance,
  GEBKeys.GrafittiDamageCoverage,
  GEBKeys.PhotovoltaicCoverage,
  GEBKeys.AcHeatpumpCoverage,
  GEBKeys.SmallOutbuildCoverage,
  GEBKeys.TheftCoverage,
  GEBKeys.GarageCoverage,
  GEBKeys.AdditionalAccessoriesCoverage,
  GEBKeys.AdditionalAccessoriesNaturalharzardCoverage,
  GEBKeys.HeritageProtectionCoverage,
  GEBKeys.DrainPipesOnPropertyCoverage,
  GEBKeys.DrainPipesOutsidePropertyCoverage,
  GEBKeys.TechCoverage
]
